(function (global) {
  'use strict';

  var SQ_FT_PER_SQ_M = 10.763910416709722;
  var SERIES = ['elastic', 'large-elite', 'elite'];
  var DEFAULT_ARTWORK_FREE_AREA_M2 = 4;
  var DEFAULT_ARTWORK_RATE_PER_M2 = 50;

  // Retail rates supplied by Swish. Each value is a price per square metre
  // (International) or square foot (USA). A court uses the greatest threshold
  // that does not exceed its exact billable area.
  var TABLES = {
    international: [
      [10, 118.8, 129.6, 140.4],
      [15, 108, 118.8, 129.6],
      [20, 91.8, 102.6, 113.4],
      [25, 81, 91.8, 102.6],
      [30, 73.44, 84.24, 95.04],
      [35, 71.28, 82.08, 92.88],
      [40, 68.04, 78.84, 89.64],
      [50, 65.88, 76.68, 87.48],
      [60, 63.72, 74.52, 85.32],
      [70, 61.56, 72.36, 83.16],
      [80, 58.32, 69.12, 79.92],
      [90, 56.16, 66.96, 77.76],
      [100, 52.92, 63.72, 74.52],
      [110, 50.76, 61.56, 72.36],
      [120, 48.6, 59.4, 70.2],
      [130, 46.44, 57.24, 68.04],
      [140, 45.36, 56.16, 66.96],
      [150, 44.28, 55.08, 65.88],
      [200, 43.2, 54, 64.8],
      [250, 42.12, 52.92, 63.72],
      [350, 41.04, 51.84, 62.64],
      [500, 39.96, 50.76, 61.56],
      [800, 36.5, 46.75, 57.1]
    ],
    usa: [
      [100, 11.59, 13.21, 14.88],
      [160, 10.48, 12.06, 13.72],
      [210, 8.28, 9.77, 11.33],
      [270, 7.72, 9.19, 10.75],
      [320, 7.18, 8.63, 10.14],
      [380, 6.96, 8.38, 9.9],
      [430, 6.62, 8.05, 9.57],
      [540, 6.39, 7.8, 9.3],
      [650, 6.18, 7.6, 9.07],
      [760, 5.97, 7.37, 8.8],
      [870, 5.63, 7.02, 8.47],
      [980, 5.41, 6.77, 8.23],
      [1100, 5.19, 6.54, 7.99],
      [1200, 4.97, 6.34, 7.52],
      [1300, 4.75, 6.07, 7.79],
      [1400, 4.53, 5.9, 7.68],
      [1500, 4.41, 5.75, 7.21],
      [1600, 4.32, 5.46, 7.18],
      [2100, 4.31, 5.5, 6.76],
      [2700, 4.19, 5.52, 6.84],
      [3800, 4.07, 5.51, 6.94],
      [5400, 3.87, 5.3, 6.71],
      [9500, 3.59, 4.17, 5.15]
    ]
  };

  function number(value) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function roundCurrency(value) {
    return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  }

  function roundTotal(value) {
    return Math.round(roundCurrency(value));
  }

  function formatTotal(value, currencyCode, locale) {
    var currency = String(currencyCode || 'EUR').toUpperCase();
    var total = roundTotal(value);
    try {
      return new Intl.NumberFormat(locale || undefined, {
        style: 'currency', currency: currency, currencyDisplay: 'symbol',
        minimumFractionDigits: 0, maximumFractionDigits: 0
      }).format(total);
    } catch (_) {
      return currency + ' ' + String(total);
    }
  }

  function calculatePayment(total, currencyCode, requestedPlan) {
    var plan = requestedPlan === 'deposit-30' ? 'deposit-30' : 'full';
    var roundedTotal = roundTotal(total);
    var dueNow = plan === 'deposit-30' ? roundTotal(roundedTotal * 0.3) : roundedTotal;
    return { plan: plan, total: roundedTotal, dueNow: dueNow,
      balance: roundedTotal - dueNow, currencyCode: currencyCode };
  }

  function normalizeSeries(value) {
    var id = String(value || '').trim().toLowerCase();
    return SERIES.indexOf(id) >= 0 ? id : 'elastic';
  }

  function resolveRegion(countryCode) {
    return String(countryCode || '').trim().toUpperCase() === 'US' ? 'usa' : 'international';
  }

  function selectBand(rows, area) {
    if (!Array.isArray(rows) || !rows.length || !Number.isFinite(area)) return null;
    var selected = rows[0];
    for (var i = 1; i < rows.length; i += 1) {
      if (area < rows[i][0]) break;
      selected = rows[i];
    }
    return selected;
  }

  function calculateArtwork(options, region) {
    options = options || {};
    var placements = Array.isArray(options.artworkPlacements) ? options.artworkPlacements : [];
    var areas = placements.reduce(function (result, placement) {
      var widthM = number(placement && placement.widthM);
      var heightM = number(placement && placement.heightM);
      if (widthM > 0 && heightM > 0) result.push(widthM * heightM);
      return result;
    }, []);
    var freeLimit = number(options.artworkFreeAreaM2);
    var ratePerM2 = number(options.artworkRatePerM2);
    if (freeLimit == null || freeLimit < 0) freeLimit = DEFAULT_ARTWORK_FREE_AREA_M2;
    if (ratePerM2 == null || ratePerM2 < 0) ratePerM2 = DEFAULT_ARTWORK_RATE_PER_M2;
    var totalAreaM2 = areas.reduce(function (total, area) { return total + area; }, 0);
    // The allowance applies to one placement only: the first active logo, or
    // the first active text when no logo is present. Unused allowance is not
    // transferred to later artwork placements.
    var includedAreaM2 = areas.length ? Math.min(areas[0], freeLimit) : 0;
    var chargeableAreaM2 = Math.max(0, totalAreaM2 - includedAreaM2);
    var ratePerDisplayUnit = region === 'usa' ? ratePerM2 / SQ_FT_PER_SQ_M : ratePerM2;
    return {
      placementCount: areas.length,
      totalAreaM2: totalAreaM2,
      totalAreaFt2: totalAreaM2 * SQ_FT_PER_SQ_M,
      includedAreaM2: includedAreaM2,
      includedAreaFt2: includedAreaM2 * SQ_FT_PER_SQ_M,
      chargeableAreaM2: chargeableAreaM2,
      chargeableAreaFt2: chargeableAreaM2 * SQ_FT_PER_SQ_M,
      freeAreaLimitM2: freeLimit,
      ratePerM2: ratePerM2,
      ratePerDisplayUnit: ratePerDisplayUnit,
      surcharge: roundCurrency(chargeableAreaM2 * ratePerM2)
    };
  }

  function calculate(options) {
    options = options || {};
    var widthM = number(options.widthM);
    var lengthM = number(options.lengthM);
    if (!(widthM > 0) || !(lengthM > 0)) return null;
    var areaM2 = widthM * lengthM;
    var region = options.region === 'usa' || options.region === 'international'
      ? options.region
      : resolveRegion(options.countryCode);
    var series = normalizeSeries(options.series);
    var seriesIndex = SERIES.indexOf(series) + 1;
    var areaUnit = region === 'usa' ? areaM2 * SQ_FT_PER_SQ_M : areaM2;
    var band = selectBand(TABLES[region], areaUnit);
    if (!band) return null;
    var unitRate = Number(band[seriesIndex]);
    var rawTotal = areaUnit * unitRate;
    var artwork = calculateArtwork(options, region);
    var courtSubtotal = roundCurrency(rawTotal);
    return {
      version: '2026-08-12',
      region: region,
      countryCode: String(options.countryCode || '').toUpperCase(),
      currencyCode: region === 'usa' ? 'USD' : 'EUR',
      areaUnit: region === 'usa' ? 'ft\u00B2' : 'm\u00B2',
      areaM2: areaM2,
      areaFt2: areaM2 * SQ_FT_PER_SQ_M,
      billableArea: areaUnit,
      bandThreshold: Number(band[0]),
      series: series,
      unitRate: unitRate,
      rawTotal: rawTotal,
      courtSubtotal: courtSubtotal,
      artwork: artwork,
      artworkSurcharge: artwork.surcharge,
      total: roundTotal(courtSubtotal + artwork.surcharge),
      shippingIncluded: true,
      taxesIncluded: true
    };
  }

  var api = {
    version: '2026-08-12',
    squareFeetPerSquareMetre: SQ_FT_PER_SQ_M,
    series: SERIES.slice(),
    tables: TABLES,
    resolveRegion: resolveRegion,
    selectBand: selectBand,
    calculateArtwork: calculateArtwork,
    roundTotal: roundTotal,
    formatTotal: formatTotal,
    calculatePayment: calculatePayment,
    calculate: calculate
  };

  global.SwishCBPricing = api;
  global.SwishCB = global.SwishCB || {};
  global.SwishCB.pricing = api;
})(typeof window !== 'undefined' ? window : globalThis);
